<template>
  <div
    class="signup-overlay restore-overlay"
    :class="{ open: isOpen }"
    data-mode="restore"
    :aria-hidden="isOpen ? 'false' : 'true'"
    @mousedown="onOverlayMouseDown"
    @mouseup="onOverlayMouseUp"
  >
    <div
      class="signup-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="dialogLabel"
    >
      <div class="signup-head"></div>

      <button
        v-if="isOpen"
        class="signup-close"
        type="button"
        :aria-label="t('close')"
        @click="requestClose"
      >
        <icon-close
          :size="14"
          :stroke-width="2.5"
        />
      </button>

      <div class="auth-single-head">
        <div class="lm-big">
          <icon-lock-open />
        </div>
      </div>

      <div class="signup-body">
        <div
          v-if="stage === 'form'"
          class="auth-view"
          data-view="restore"
        >
          <h2>
            {{ t('restore_title_prefix') }}<span class="grad">{{ t('restore_title_grad') }}</span>{{ t('restore_title_suffix') }}
          </h2>
          <p class="sub">{{ t('restore_sub') }}</p>

          <form
            novalidate
            @submit.prevent="submit"
          >
            <div class="signup-field">
              <label
                class="field-label"
                id="restore-password-label"
              >{{ t('lbl_restore_password') }}</label>
              <div class="pw-wrap">
                <input
                  id="restore-password"
                  v-model="password"
                  :type="showPw ? 'text' : 'password'"
                  class="signup-input"
                  aria-labelledby="restore-password-label"
                  :class="{ error: !!errors.password }"
                  :placeholder="t('ph_restore_password')"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="pw-toggle"
                  :aria-label="t('lbl_restore_password')"
                  @click="showPw = !showPw"
                >
                  <icon-eye :size="18" />
                </button>
              </div>
              <div
                v-if="errors.password"
                class="err"
              >
                <icon-info-circle
                  :size="13"
                  :stroke-width="2.4"
                />
                <span>{{ errors.password }}</span>
              </div>
            </div>

            <div class="signup-field">
              <label
                class="field-label"
                id="restore-password-confirm-label"
              >{{ t('lbl_restore_password_confirm') }}</label>
              <div class="pw-wrap">
                <input
                  id="restore-password-confirm"
                  v-model="passwordConfirmation"
                  :type="showConfirm ? 'text' : 'password'"
                  class="signup-input"
                  aria-labelledby="restore-password-confirm-label"
                  :class="{ error: !!errors.password_confirmation }"
                  :placeholder="t('ph_restore_confirm')"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="pw-toggle"
                  :aria-label="t('lbl_restore_password_confirm')"
                  @click="showConfirm = !showConfirm"
                >
                  <icon-eye :size="18" />
                </button>
              </div>
              <div
                v-if="errors.password_confirmation"
                class="err"
              >
                <icon-info-circle
                  :size="13"
                  :stroke-width="2.4"
                />
                <span>{{ errors.password_confirmation }}</span>
              </div>
            </div>

            <div
              v-if="submitError"
              class="submit-err show"
            >
              <icon-info-circle
                :size="16"
                :stroke-width="2.4"
              />
              <span>{{ submitError }}</span>
            </div>

            <button
              type="submit"
              class="sb-next wide"
              :disabled="submitting"
            >
              <template v-if="submitting">
                <span
                  class="spinner"
                  aria-hidden="true"
                ></span>
                {{ t('btn_loading') }}
              </template>
              <template v-else>{{ t('restore_btn') }} <span>→</span></template>
            </button>
          </form>
        </div>

        <div
          v-else
          class="auth-confirm"
        >
          <div class="icon success">
            <icon-check
              :size="42"
              :stroke-width="2.6"
            />
          </div>
          <h2>
            {{ t('restore_done_title_prefix') }}
            <span class="grad">{{ t('restore_done_title_grad') }}</span>
            {{ t('restore_done_title_suffix') }}
          </h2>
          <p>{{ t('restore_done_body') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { i18nMixin, refreshCsrfToken } from './helpers';
import IconClose from '../icons/IconClose.vue';
import IconCheck from '../icons/IconCheck.vue';
import IconEye from '../icons/IconEye.vue';
import IconInfoCircle from '../icons/IconInfoCircle.vue';
import IconLockOpen from '../icons/IconLockOpen.vue';

const PATH_RE = /\/restore-account\/([^/?#]+)/;

export default {
  name: 'RestoreAccountModal',
  mixins: [i18nMixin],
  components: { IconClose, IconCheck, IconEye, IconInfoCircle, IconLockOpen },
  data() {
    return {
      isOpen: false,
      stage: 'form', // 'form' | 'done'
      hash: '',
      password: '',
      passwordConfirmation: '',
      showPw: false,
      showConfirm: false,
      submitting: false,
      submitError: '',
      errors: {},
      backdropMouseDown: false,
      // Scroll offset captured by the body scroll-lock, restored on unlock.
      scrollY: 0,
    };
  },
  computed: {
    dialogLabel() {
      return `${this.t('restore_title_prefix')}${this.t('restore_title_grad')}${this.t(
        'restore_title_suffix'
      )}`;
    },
  },
  mounted() {
    this.$eventHub.$on('viluu-landing:open-restore-account-modal', this.handleOpen);
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    this.$eventHub.$off('viluu-landing:open-restore-account-modal', this.handleOpen);
    document.removeEventListener('keydown', this.onKeydown);
    this.unlockScroll();
  },
  methods: {
    handleOpen() {
      const match = (window.location.pathname || '').match(PATH_RE);
      if (!match) {
        return;
      }
      this.hash = decodeURIComponent(match[1]);
      this.open();
    },
    // See SignupModal: `overflow: hidden` on <body> doesn't hold on iOS, so we
    // pin it with `position: fixed` at a negative top and restore on unlock.
    lockScroll() {
      this.scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${this.scrollY}px`;
      document.body.classList.add('signup-locked');
    },
    unlockScroll() {
      if (!document.body.classList.contains('signup-locked')) {
        return;
      }
      document.body.classList.remove('signup-locked');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY);
    },
    open() {
      this.isOpen = true;
      this.lockScroll();
    },
    close() {
      this.isOpen = false;
      this.unlockScroll();
      // Clean the URL so refreshing/sharing the page lands on the landing root
      // instead of re-triggering the (now-consumed) restore flow.
      if (window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState({}, '', '/');
      }
    },
    requestClose() {
      if (this.submitting) {
        return;
      }
      this.close();
    },
    onOverlayMouseDown(e) {
      this.backdropMouseDown = e.target === e.currentTarget;
    },
    onOverlayMouseUp(e) {
      const startedAndEndedOnBackdrop = this.backdropMouseDown && e.target === e.currentTarget;
      this.backdropMouseDown = false;
      if (startedAndEndedOnBackdrop) {
        this.requestClose();
      }
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.requestClose();
      }
    },
    async submit() {
      if (this.submitting) {
        return;
      }
      this.errors = {};
      this.submitError = '';

      if (!this.password) {
        this.errors = { password: this.t('submit_err_generic') };
        return;
      }
      if (this.password !== this.passwordConfirmation) {
        this.errors = { password_confirmation: this.t('restore_match_err') };
        return;
      }

      this.submitting = true;
      await refreshCsrfToken();
      try {
        const response = await axios.post(zRoute('ajax.restore_account'), {
          hash: this.hash,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
        });
        if (response.data && response.data.status === 'success') {
          this.stage = 'done';
          window.location.href = zRoute('home');
          return;
        }
        this.submitError = this.t('restore_err_generic');
      } catch (err) {
        const data = err && err.response && err.response.data;
        if (data && data.errors) {
          const flat = {};
          Object.keys(data.errors).forEach((k) => {
            const v = data.errors[k];
            flat[k] = Array.isArray(v) ? v[0] : v;
          });
          this.errors = flat;
          if (flat.hash) {
            this.submitError = flat.hash;
          }
        } else {
          this.submitError = this.t('restore_err_generic');
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
